<template>
  <div class="detail-container">
    <div class="page-header">
      <h1 class="page-title">取貨預約詳情</h1>
      <div class="action-buttons">
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchAppointment">重試</button>
    </div>

    <div v-else-if="appointment" class="detail-content">
      <div class="detail-section">
        <h3 class="section-title">取貨資訊</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>取貨日期</label>
            <span>{{ formatDate(appointment.pickupDate) }}</span>
          </div>
          <div class="detail-item">
            <label>取貨時段</label>
            <span>{{ appointment.pickupTimeSlot || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>狀態</label>
            <span :class="getStatusBadgeClass(appointment.status)">
              {{ getStatusText(appointment.status) }}
            </span>
          </div>
          <div class="detail-item">
            <label>訂單編號</label>
            <span>{{ appointment.orderId || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h3 class="section-title">時間記錄</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>建立時間</label>
            <span>{{ formatDateTime(appointment.createdAt) }}</span>
          </div>
          <div v-if="appointment.pickedUpAt" class="detail-item">
            <label>取貨時間</label>
            <span>{{ formatDateTime(appointment.pickedUpAt) }}</span>
          </div>
          <div v-if="appointment.pickedUpBy" class="detail-item">
            <label>取貨人員</label>
            <span>{{ appointment.pickedUpBy }}</span>
          </div>
        </div>
      </div>

      <div v-if="appointment.calendarEventId" class="detail-section">
        <h3 class="section-title">Google Calendar</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>同步狀態</label>
            <span :class="getSyncStatusBadgeClass(appointment.calendarSyncStatus)">
              {{ getSyncStatusText(appointment.calendarSyncStatus) }}
            </span>
          </div>
          <div v-if="appointment.calendarSyncedAt" class="detail-item">
            <label>最後同步時間</label>
            <span>{{ formatDateTime(appointment.calendarSyncedAt) }}</span>
          </div>
        </div>
      </div>

      <div v-if="appointment.status === 'ready'" class="detail-actions">
        <button class="btn btn-success" @click="handleConfirmPickup">
          <i class="ri-check-line"></i>
          確認取貨
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { pickupAppointmentService } from '@/api/modules/pickupAppointments.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'PickupAppointmentDetail',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const appointment = ref(null);
    const loading = ref(false);
    const error = ref(null);

    const fetchAppointment = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await pickupAppointmentService.getPickupAppointment(route.params.id);
        // axios 攔截器已經返回 response.data，所以 response 就是 { success, data, message }
        if (response?.success && response?.data) {
          appointment.value = response.data;
        } else {
          throw new Error('查詢失敗');
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '查詢取貨預約詳情失敗';
        console.error('Failed to fetch appointment:', err);
      } finally {
        loading.value = false;
      }
    };

    const handleBack = () => {
      router.push('/client/pickup-appointments');
    };

    const handleConfirmPickup = async () => {
      try {
        await pickupAppointmentService.confirmPickup(route.params.id, {
          pickedUpBy: '管理員'
        });
        showSuccess('已確認取貨');
        fetchAppointment();
      } catch (err) {
        showError(err.response?.data?.message || '確認取貨失敗');
      }
    };

    const getStatusText = (status) => {
      const statusMap = {
        'pending': '待取貨',
        'ready': '已備貨',
        'picked_up': '已取貨',
        'expired': '已過期'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        'pending': 'badge badge-warning',
        'ready': 'badge badge-info',
        'picked_up': 'badge badge-success',
        'expired': 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const getSyncStatusText = (status) => {
      const statusMap = {
        'pending': '待同步',
        'synced': '已同步',
        'failed': '同步失敗'
      };
      return statusMap[status] || status;
    };

    const getSyncStatusBadgeClass = (status) => {
      const classMap = {
        'pending': 'badge badge-warning',
        'synced': 'badge badge-success',
        'failed': 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('zh-TW');
    };

    const formatDateTime = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW');
    };

    onMounted(() => {
      fetchAppointment();
    });

    return {
      appointment,
      loading,
      error,
      fetchAppointment,
      handleBack,
      handleConfirmPickup,
      getStatusText,
      getStatusBadgeClass,
      getSyncStatusText,
      getSyncStatusBadgeClass,
      formatDate,
      formatDateTime
    };
  }
};
</script>

<style scoped>
.detail-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.detail-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.detail-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-item label {
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
  font-size: 14px;
}

.detail-item span {
  font-size: 16px;
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

@media (max-width: 768px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
